import { useEffect, useMemo, useState } from 'react';
import { DollarSign, TrendingUp, Clock, CheckCircle, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { commissionService } from '../../services/commissionService';
import { UI, ui } from '../../utils/ui';

const statusPill = (status) => {
  const map = {
    pending: { backgroundColor: '#fef9c3', color: '#854d0e', borderColor: '#fde047' },
    paid: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' },
  };
  const c = map[status] || map.pending;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 900,
    border: `1px solid ${c.borderColor}`,
    backgroundColor: c.backgroundColor,
    color: c.color,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  };
};

const MyCommissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({ total_earned: 0, pending_amount: 0, paid_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    commissionService.getMyCommissions()
      .then((res) => {
        setCommissions(res.data.commissions || []);
        setSummary(res.data.summary || {});
      })
      .catch(() => toast.error('Failed to load commissions.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => (filter === 'all' ? commissions : commissions.filter((c) => c.status === filter)), [commissions, filter]);

  const summaryCards = [
    { label: 'Total Earned', value: `₹${Number(summary.total_earned || 0).toLocaleString()}`, icon: TrendingUp, accent: '#16a34a', bg: 'rgba(22,163,74,0.12)' },
    { label: 'Pending', value: `₹${Number(summary.pending_amount || 0).toLocaleString()}`, icon: Clock, accent: UI.colors.warning, bg: 'rgba(245,158,11,0.14)' },
    { label: 'Paid', value: `₹${Number(summary.paid_amount || 0).toLocaleString()}`, icon: CheckCircle, accent: UI.colors.info, bg: 'rgba(37,99,235,0.12)' },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const wrapStyle = { display: 'flex', flexDirection: 'column', gap: 18 };
  const titleStyle = { fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text };
  const subtitleStyle = { margin: '8px 0 0', fontSize: 14, color: UI.colors.muted };
  const sectionTitleStyle = { fontFamily: UI.font.heading, fontSize: 18, fontWeight: 900, color: UI.colors.text, margin: 0 };

  return (
    <div style={wrapStyle}>
      {/* Header */}
      <div>
        <h1 style={titleStyle}>My Commissions</h1>
        <p style={subtitleStyle}>Track your earnings from bookings</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        {summaryCards.map((s) => (
          <div key={s.label} style={{ ...ui.card, padding: 20 }}>
            <div style={{ width: 46, height: 46, borderRadius: 14, backgroundColor: s.bg, color: s.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <s.icon size={22} />
            </div>
            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 900, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.label}</p>
            <p style={{ margin: 0, fontFamily: UI.font.heading, fontSize: 24, fontWeight: 900, color: UI.colors.text }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Commission Table */}
      <div style={ui.card}>
        <div style={{ padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <h2 style={sectionTitleStyle}>Commission History</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
              <Filter size={16} color="#9ca3af" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{ border: 'none', outline: 'none', backgroundColor: 'transparent', fontSize: 14, fontWeight: 800, color: UI.colors.text, cursor: 'pointer' }}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 18px', color: UI.colors.muted }}>
              <DollarSign size={44} color="#9ca3af" style={{ opacity: 0.3, margin: '0 auto 12px' }} />
              <p style={{ margin: 0, fontWeight: 900 }}>No commissions found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 820 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 12, padding: '10px 12px', borderRadius: 14, backgroundColor: '#f3f4f6', border: `1px solid ${UI.colors.border}`, fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10 }}>
                  <span>Booking Ref</span><span>Customer</span><span>Room</span><span>Booking Total</span><span>Commission</span><span>Date</span><span>Status</span>
                </div>
                {filtered.map((c) => (
                  <div key={c.id} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 12, padding: '12px 12px', borderBottom: `1px solid ${UI.colors.border}`, alignItems: 'center', fontSize: 13 }}>
                    <span style={{ color: UI.colors.primary, fontWeight: 900, fontSize: 12 }}>{c.booking_reference}</span>
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>{c.customer_name}</span>
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>{c.room_name}</span>
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>₹{Number(c.booking_amount).toLocaleString()}</span>
                    <span style={{ color: '#16a34a', fontWeight: 900 }}>₹{Number(c.commission_amount).toLocaleString()}</span>
                    <span style={{ color: '#9ca3af', fontWeight: 800, fontSize: 12 }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</span>
                    <span style={statusPill(c.status)}>{c.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyCommissions;

